// Shared class strings live in components/Broadcast/ui.js; re-exported here so
// the Drop Checker screens match the rest of the admin without a second copy.
import { inputClass, btnBase, btnPrimary, btnGhost, btnDanger, iconBtn, th } from '../Broadcast/ui';

export { inputClass, btnBase, btnPrimary, btnGhost, btnDanger, iconBtn, th };

export const tabBase = 'px-3 py-1.5 rounded-md text-sm font-medium transition-colors';
export const tabOn = 'bg-primary text-primary-foreground';
export const tabOff = 'text-muted-foreground hover:bg-secondary/50 hover:text-foreground';

export const checkboxClass = 'h-4 w-4 rounded border-input text-primary focus:ring-ring';

/** Verdict pill for a checked order. */
export function verdictPill(row) {
    if (!row || (row.current === undefined && row.missing === undefined)) {
        return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground';
    }
    if (row.dropped) {
        return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    }
    if (row.unknown) {
        return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    }
    return 'inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
}

export function verdictLabel(row) {
    if (!row || (row.current === undefined && row.missing === undefined)) return 'Not checked';
    if (row.dropped) return 'Dropped';
    if (row.unknown) return 'Unknown';
    return 'OK';
}

/** Order status pill, reusing the same tones as the rest of the admin. */
export function orderStatusPill(status) {
    const tones = {
        completed: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        partial: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
        in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        processing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        pending: 'bg-muted text-muted-foreground',
        canceled: 'bg-muted text-muted-foreground',
        error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
        fail: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    const tone = tones[status] || 'bg-muted text-muted-foreground';
    return `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tone}`;
}

export function n(value) {
    return value === null || value === undefined || value === '' ? '—' : String(value);
}

/** Background-run status pill. */
export function jobStatusPill(status) {
    const tones = {
        running: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        done: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        stopped: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
        failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
    };
    const tone = tones[status] || 'bg-muted text-muted-foreground';
    return `inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${tone}`;
}

/** MySQL DATETIME (or ISO string) → something readable in a table cell. */
export function shortDateTime(value) {
    if (!value) return '—';
    return String(value).replace('T', ' ').slice(0, 16);
}

/** Clipboard write with a fallback for browsers that block the async API. */
export async function copyText(text) {
    if (!text) return false;
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (err) {
        // fall through to the textarea path
    }
    try {
        const area = document.createElement('textarea');
        area.value = text;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.top = '-1000px';
        document.body.appendChild(area);
        area.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(area);
        return ok;
    } catch (err) {
        return false;
    }
}

export const FIELD_OPTIONS = [
    { key: 'orderId', label: 'Order ID (ours)', defaultOn: true },
    { key: 'externalId', label: 'External ID (provider)', defaultOn: true },
    { key: 'link', label: 'Link', defaultOn: false },
    { key: 'details', label: 'Order details (status, counts, missing)', defaultOn: false }
];

export const defaultFields = () =>
    FIELD_OPTIONS.reduce((acc, f) => ({ ...acc, [f.key]: f.defaultOn }), {});

/**
 * Render the copy text. `layout` decides whether every order gets its own line
 * (for a ticket) or the values are flattened into one line (for an ID list).
 */
export function buildCopyText(rows, fields, layout, separator) {
    const lines = (rows || []).map(row => {
        const parts = [];
        if (fields.orderId) parts.push(String(row.id));
        if (fields.externalId) parts.push(row.external_id || '-');
        if (fields.link) parts.push(row.link || '-');
        if (fields.details) {
            parts.push(
                `${row.status || '-'}; start ${row.start_count ?? '-'}; qty ${row.quantity ?? '-'}; `
                + `remains ${row.remains ?? '-'}; now ${row.current ?? 'n/a'}; missing ${row.missing ?? 'n/a'}`
            );
        }
        return parts.join(' | ');
    }).filter(Boolean);

    if (!lines.length) return '';
    if (layout === 'single') return lines.join(separator);
    return lines.join('\n');
}
